import { PriceIdEnum, ProductWeightsEnum } from '@iheartjane/types';
import type {
  KindCondition,
  Nullish,
  PriceId,
  ProductCategory,
  ProductKind,
  ProductMatchAxes,
  SpecialRuleSection,
  SpecialRuleSetsFor,
  WeightValue,
} from '@iheartjane/types';
import castArray from 'lodash-es/castArray';
import uniq from 'lodash-es/uniq';

import { GRAMS_TO_PRICE_ID } from '../priceIdToGrams';

const priceIdForGrams = ({ value }: WeightValue) =>
  GRAMS_TO_PRICE_ID.get(value);

// Mirrors Specials::Rules::Weights.convert_weight_values_to_price_ids: a
// weight with no standard price id reads as each.
const priceIdsForWeightValues = (
  weightValues: Nullish<WeightValue[]>
): PriceId[] =>
  uniq(
    (weightValues ?? []).map(
      (weightValue) => priceIdForGrams(weightValue) ?? 'each'
    )
  );

// Narrowed to the weight axis so every special shape (v3, legacy, Bloom) fits.
type WeightRule = { weights?: Nullish<PriceId[]> };

export type WeightRuleSection = {
  excludes?: Nullish<WeightRule[]>;
  includes?: Nullish<WeightRule[]>;
  rule_sets?: Nullish<SpecialRuleSection['rule_sets']>;
};

type BulkWeight = {
  maximum_price?: Nullish<number>;
  minimum_price?: Nullish<number>;
  price_id?: PriceId;
};

type BulkWeightRuleSection = {
  includes?: Nullish<{ bulk_weights?: Nullish<BulkWeight[]> }[]>;
  rule_sets?: Nullish<SpecialRuleSetsFor<'bulk_pricing'>>;
};

export type WeightRuleProduct = {
  brand_subtype?: Nullish<string>;
  category?: Nullish<ProductCategory>;
  kind: ProductKind;
  product_brand_id?: Nullish<number | string>;
  product_id?: Nullish<number>;
  root_subtype?: Nullish<string>;
};
interface ShouldReadRuleSetsArgs {
  multipleConditions?: boolean;
  section?: Nullish<WeightRuleSection | BulkWeightRuleSection>;
}

/**
 * With the flag on, rule_sets is the source of truth; legacy includes/excludes
 * are only read for specials that have no rule_sets.
 */
export const shouldReadRuleSets = ({
  multipleConditions,
  section,
}: ShouldReadRuleSetsArgs) =>
  Boolean(multipleConditions) && (section?.rule_sets?.length ?? 0) > 1;

const axisMatches = <Value>(
  values: Nullish<Value[]>,
  productValue: Nullish<Value>
) =>
  !values?.length ||
  (productValue !== null &&
    productValue !== undefined &&
    values.includes(productValue));

// Mirrors Rules::ConditionGroup#kind_root_subtype_allowed?
const rootSubtypeMatches = (
  rootSubtype: Nullish<string>,
  product: WeightRuleProduct
) => {
  if (!rootSubtype) return true;

  const normalizedRootSubtype = rootSubtype.toLowerCase();
  if (
    product.kind === 'flower' &&
    normalizedRootSubtype === 'flower' &&
    !product.root_subtype
  ) {
    return true;
  }

  return normalizedRootSubtype === product.root_subtype?.toLowerCase();
};

const kindMatches = (condition: KindCondition, product: WeightRuleProduct) =>
  condition.kind === product.kind &&
  rootSubtypeMatches(condition.root_subtype, product) &&
  (!condition.brand_subtype ||
    condition.brand_subtype === product.brand_subtype);

// Mirrors the includes side of Rules::ConditionGroup#passes?. Axes we don't
// check (pos_tags, ...) count as a match: better to show extra weights than
// hide a discount.
const productMatchesIncludes = (
  includes: Nullish<ProductMatchAxes>,
  product: WeightRuleProduct
) =>
  axisMatches(includes?.product_ids, product.product_id) &&
  axisMatches(
    includes?.brand_ids,
    product.product_brand_id == null ? null : Number(product.product_brand_id)
  ) &&
  axisMatches(includes?.lineages, product.category) &&
  (!includes?.kinds?.length ||
    includes.kinds.some((kind) => kindMatches(kind, product)));

/**
 * A product only gets the weights of the groups it belongs to, e.g. flower
 * 3.5g + any vape gives a flower card 3.5g only. Falls back to every group
 * when none match.
 */
const groupsForRuleSets = <
  Group extends { includes?: Nullish<ProductMatchAxes> },
>(
  ruleSets: Nullish<readonly ['any', ...Group[]]>,
  product?: Nullish<WeightRuleProduct>
) => {
  const [, ...groups] = ruleSets ?? ['any'];
  if (!product) return groups;

  const productGroups = groups.filter(({ includes }) =>
    productMatchesIncludes(includes, product)
  );

  return productGroups.length ? productGroups : groups;
};

// No includes weights means every weight qualifies, less the excluded ones.
const priceIdsForGroup = (
  group: { excludes?: Nullish<WeightRule>; includes?: Nullish<WeightRule> },
  expandEachWeight: boolean
): PriceId[] => {
  let includesWeights = group.includes?.weights ?? [];
  const excludesWeights = group.excludes?.weights ?? [];

  if (expandEachWeight && includesWeights.includes('each')) {
    includesWeights = [...ProductWeightsEnum];
  }

  const allowedWeights = includesWeights.length
    ? includesWeights
    : [...PriceIdEnum];

  return allowedWeights.filter((weight) => !excludesWeights.includes(weight));
};

interface PriceIdsForWeightRulesArgs {
  expandEachWeight?: boolean;
  multipleConditions?: boolean;
  product?: Nullish<WeightRuleProduct>;
  section?: Nullish<WeightRuleSection>;
}

/**
 * Price ids a rules section allows. A special matches on any rule_sets group,
 * so the allowed weights are the union across groups (the product's groups,
 * when given).
 */
export const priceIdsForWeightRules = ({
  expandEachWeight = false,
  multipleConditions,
  product,
  section,
}: PriceIdsForWeightRulesArgs): PriceId[] => {
  if (!shouldReadRuleSets({ multipleConditions, section })) {
    return priceIdsForGroup(
      { excludes: section?.excludes?.[0], includes: section?.includes?.[0] },
      expandEachWeight
    );
  }

  return uniq(
    groupsForRuleSets(section?.rule_sets, product).flatMap(
      ({ excludes, includes }) =>
        priceIdsForGroup(
          {
            excludes: {
              weights: priceIdsForWeightValues(
                castArray(excludes ?? []).flatMap(
                  ({ weight_values }) => weight_values ?? []
                )
              ),
            },
            includes: {
              weights: priceIdsForWeightValues(includes?.weight_values),
            },
          },
          expandEachWeight
        )
    )
  );
};

interface BulkWeightsForRulesArgs {
  multipleConditions?: boolean;
  product?: Nullish<WeightRuleProduct>;
  rules?: Nullish<BulkWeightRuleSection>;
}

/**
 * bulk_weights entries a bulk_pricing special allows, across every rule_sets
 * group. The same price_id can appear once per group with different price
 * thresholds. Empty means every weight qualifies.
 */
export const bulkWeightsForRules = ({
  multipleConditions,
  product,
  rules,
}: BulkWeightsForRulesArgs): BulkWeight[] => {
  if (!shouldReadRuleSets({ multipleConditions, section: rules })) {
    return rules?.includes?.[0]?.bulk_weights ?? [];
  }

  const groups = groupsForRuleSets(rules?.rule_sets, product);

  // A group without bulk weights matches any weight (Rules::ConditionGroup)
  if (groups.some(({ includes }) => !includes?.bulk_weight_values?.length)) {
    return [];
  }

  return groups.flatMap(({ includes }) =>
    (includes?.bulk_weight_values ?? []).map(({ weight_value, ...prices }) => ({
      ...prices,
      price_id: priceIdForGrams(weight_value),
    }))
  );
};
